import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Punch History Page (Admin & Manager View)
 *
 * Allows admins and managers to view punch history for employees
 * - Admins: Can view all employees
 * - Managers: Can view only direct reports
 *
 * Features:
 * - Employee selector with search
 * - Date range selection
 * - Daily totals showing hours worked
 * - Detailed punch records
 */
import { useState, useMemo } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, subDays, startOfWeek, addDays, differenceInDays } from 'date-fns';
import { Clock, Search, Calendar, User, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { useEmployees } from '../../hooks/useEmployees';
import { useAuthStore, selectIsAdmin } from '../../stores/auth.store';
import { config } from '../../lib/config';
import { listPayPeriodConfigs } from '../../lib/api/time.client';
const TIME_API_URL = config.api.time;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const tokens = useAuthStore.getState().tokens;
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens?.idToken || ''}`,
    };
}
/**
 * Fetch punch history for specific employee and date range
 */
async function getPunchHistory(employeeId, startDate, endDate) {
    const params = new URLSearchParams({
        employeeId,
        startDate,
        endDate,
    });
    const response = await fetch(`${TIME_API_URL}/punches?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch punch history: ${response.statusText}`);
    }
    return response.json();
}
/**
 * Calculate current pay period dates based on configuration
 */
function calculateCurrentPayPeriod(config) {
    const today = new Date();
    const referenceDate = config.referenceDate ? parseISO(config.referenceDate) : today;
    switch (config.frequency) {
        case 'weekly': {
            const startDayOfWeek = config.startDayOfWeek ?? 0; // Default to Sunday
            const currentWeekStart = startOfWeek(today, { weekStartsOn: startDayOfWeek });
            return {
                startDate: format(currentWeekStart, 'yyyy-MM-dd'),
                endDate: format(addDays(currentWeekStart, 6), 'yyyy-MM-dd'),
            };
        }
        case 'biweekly': {
            const daysSinceReference = differenceInDays(today, referenceDate);
            const periodsSinceReference = Math.floor(daysSinceReference / 14);
            const currentPeriodStart = addDays(referenceDate, periodsSinceReference * 14);
            return {
                startDate: format(currentPeriodStart, 'yyyy-MM-dd'),
                endDate: format(addDays(currentPeriodStart, 13), 'yyyy-MM-dd'),
            };
        }
        case 'semimonthly': {
            const day = today.getDate();
            if (day <= 15) {
                return {
                    startDate: format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'),
                    endDate: format(new Date(today.getFullYear(), today.getMonth(), 15), 'yyyy-MM-dd'),
                };
            }
            else {
                return {
                    startDate: format(new Date(today.getFullYear(), today.getMonth(), 16), 'yyyy-MM-dd'),
                    endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
                };
            }
        }
        case 'monthly': {
            return {
                startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
                endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
            };
        }
        default:
            // Fallback to current month
            return {
                startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
                endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
            };
    }
}
/**
 * Calculate daily totals from punch records
 */
function calculateDailyTotals(punches) {
    const dailyMap = new Map();
    // Sort punches by timestamp (oldest first) to process them in chronological order
    // API returns them newest-first, but we need to process CLOCK_IN before CLOCK_OUT, etc.
    const sortedPunches = [...punches].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    // Group punches by date
    for (const punch of sortedPunches) {
        if (punch.status === 'CORRECTED')
            continue; // Skip corrected punches
        // Extract date from timestamp in LOCAL timezone (not UTC)
        // This ensures punches are grouped by the local date when they occurred
        const date = punch.timestamp ? format(parseISO(punch.timestamp), 'yyyy-MM-dd') : null;
        if (!date)
            continue; // Skip if no date available
        if (!dailyMap.has(date)) {
            dailyMap.set(date, { clockIn: null, clockOut: null, breaks: [], punchCount: 0 });
        }
        const day = dailyMap.get(date);
        day.punchCount++;
        if (punch.action === 'CLOCK_IN' && !day.clockIn) {
            day.clockIn = new Date(punch.timestamp);
        }
        else if (punch.action === 'CLOCK_OUT') {
            day.clockOut = new Date(punch.timestamp);
        }
        else if (punch.action === 'BREAK_START') {
            day.breaks.push({ start: new Date(punch.timestamp), end: null });
        }
        else if (punch.action === 'BREAK_END' && day.breaks.length > 0) {
            const lastBreak = day.breaks[day.breaks.length - 1];
            if (lastBreak && !lastBreak.end) {
                lastBreak.end = new Date(punch.timestamp);
            }
        }
    }
    // Calculate totals
    const dailyTotals = [];
    for (const [date, day] of dailyMap.entries()) {
        let hoursWorked = 0;
        let breakTime = 0;
        // Calculate work hours
        if (day.clockIn && day.clockOut) {
            hoursWorked = (day.clockOut.getTime() - day.clockIn.getTime()) / (1000 * 60 * 60);
        }
        // Calculate break time
        for (const breakPeriod of day.breaks) {
            if (breakPeriod.end) {
                breakTime += (breakPeriod.end.getTime() - breakPeriod.start.getTime()) / (1000 * 60 * 60);
            }
        }
        const netHours = hoursWorked - breakTime;
        dailyTotals.push({
            date,
            hoursWorked,
            breakTime,
            netHours,
            punchCount: day.punchCount,
        });
    }
    // Sort by date descending (most recent first)
    return dailyTotals.sort((a, b) => b.date.localeCompare(a.date));
}
export default function PunchHistory() {
    const isAdmin = useAuthStore(selectIsAdmin);
    const { data: employees, isLoading: loadingEmployees } = useEmployees();
    // Date range state (default: last 14 days)
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 14), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    // Employee selection state
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    // Expanded days state (track which days are expanded)
    const [expandedDays, setExpandedDays] = useState(new Set());
    // Filter employees by search
    const filteredEmployees = useMemo(() => {
        if (!employees)
            return [];
        const query = searchQuery.toLowerCase();
        return employees
            .filter(emp => emp.status === 'active')
            .filter(emp => emp.firstName.toLowerCase().includes(query) ||
            emp.lastName.toLowerCase().includes(query) ||
            emp.position?.toLowerCase().includes(query));
    }, [employees, searchQuery]);
    // Get selected employee info
    const selectedEmployee = useMemo(() => {
        if (!employees || !selectedEmployeeId)
            return null;
        return employees.find(emp => emp.employeeId === selectedEmployeeId);
    }, [employees, selectedEmployeeId]);
    // Fetch pay period configurations
    const { data: payPeriodData } = useQuery({
        queryKey: ['pay-period-configs'],
        queryFn: listPayPeriodConfigs,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
    const defaultPayPeriodConfig = useMemo(() => {
        return payPeriodData?.configs?.find(c => c.isDefault) || payPeriodData?.configs?.[0];
    }, [payPeriodData]);
    // Fetch punch history for selected employee
    const { data: punchData, isLoading: loadingPunches, error: punchError } = useQuery({
        queryKey: ['punch-history', selectedEmployeeId, startDate, endDate],
        queryFn: () => getPunchHistory(selectedEmployeeId, startDate, endDate),
        enabled: !!selectedEmployeeId && !!startDate && !!endDate,
        staleTime: 60 * 1000, // 1 minute
    });
    const punches = punchData?.punches || [];
    const dailyTotals = useMemo(() => calculateDailyTotals(punches), [punches]);
    // Calculate summary stats
    const summaryStats = useMemo(() => {
        const totalHours = dailyTotals.reduce((sum, day) => sum + day.netHours, 0);
        const totalBreakTime = dailyTotals.reduce((sum, day) => sum + day.breakTime, 0);
        const daysWorked = dailyTotals.filter(day => day.netHours > 0).length;
        return { totalHours, totalBreakTime, daysWorked };
    }, [dailyTotals]);
    const handleEmployeeSelect = (employeeId) => {
        setSelectedEmployeeId(employeeId);
        setShowDropdown(false);
        setSearchQuery('');
        setExpandedDays(new Set()); // Reset expanded days when changing employee
    };
    const handleQuickDateRange = (range) => {
        const today = new Date();
        switch (range) {
            case 'week':
                setStartDate(format(subDays(today, 7), 'yyyy-MM-dd'));
                setEndDate(format(today, 'yyyy-MM-dd'));
                break;
            case 'month':
                setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
                setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
                break;
            case 'lastMonth':
                const lastMonth = subDays(startOfMonth(today), 1);
                setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
                setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
                break;
            case 'payPeriod':
                if (defaultPayPeriodConfig) {
                    const period = calculateCurrentPayPeriod(defaultPayPeriodConfig);
                    setStartDate(period.startDate);
                    setEndDate(period.endDate);
                }
                break;
        }
        setExpandedDays(new Set()); // Reset expanded days when changing date range
    };
    const toggleDayExpanded = (date) => {
        setExpandedDays(prev => {
            const next = new Set(prev);
            if (next.has(date)) {
                next.delete(date);
            }
            else {
                next.add(date);
            }
            return next;
        });
    };
    const formatHours = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    };
    const getActionBadge = (action) => {
        switch (action) {
            case 'CLOCK_IN':
                return _jsx(Badge, { className: "bg-green-100 text-green-800", children: "Clock In" });
            case 'CLOCK_OUT':
                return _jsx(Badge, { className: "bg-red-100 text-red-800", children: "Clock Out" });
            case 'BREAK_START':
                return _jsx(Badge, { className: "bg-yellow-100 text-yellow-800", children: "Break Start" });
            case 'BREAK_END':
                return _jsx(Badge, { className: "bg-blue-100 text-blue-800", children: "Break End" });
            default:
                return _jsx(Badge, { variant: "secondary", children: action });
        }
    };
    if (loadingEmployees) {
        return (_jsxs("div", { className: "space-y-6", children: [_jsx(Skeleton, { className: "h-32 w-full" }), _jsx(Skeleton, { className: "h-64 w-full" })] }));
    }
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold text-gray-900", children: "Punch History" }), _jsxs("p", { className: "text-gray-600 mt-1", children: ["View time clock records for ", isAdmin ? 'all employees' : 'your direct reports'] })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(User, { className: "h-5 w-5" }), "Select Employee"] }), _jsx(CardDescription, { children: "Choose an employee to view their punch history" })] }), _jsx(CardContent, { children: _jsxs("div", { className: "relative", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" }), _jsx(Input, { placeholder: selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'Search employees...', value: searchQuery, onChange: (e) => {
                                                setSearchQuery(e.target.value);
                                                setShowDropdown(true);
                                            }, onFocus: () => setShowDropdown(true), className: "pl-10" })] }), showDropdown && searchQuery && (_jsx("div", { className: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto", children: filteredEmployees.length === 0 ? (_jsxs("div", { className: "p-4 text-center text-gray-500", children: [_jsx("p", { className: "font-medium", children: "No employees found" }), _jsx("p", { className: "text-sm mt-1", children: "Try a different search term" })] })) : (_jsx("div", { className: "py-1", children: filteredEmployees.map((employee) => (_jsxs("button", { type: "button", onClick: () => handleEmployeeSelect(employee.employeeId), className: "w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-3", children: [_jsxs("div", { className: "flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-semibold", children: [employee.firstName.charAt(0), employee.lastName.charAt(0)] }), _jsxs("div", { className: "flex-1", children: [_jsxs("p", { className: "text-sm font-medium text-gray-900", children: [employee.firstName, " ", employee.lastName] }), _jsx("p", { className: "text-xs text-gray-500", children: employee.position || '—' })] })] }, employee.employeeId))) })) })), selectedEmployee && !searchQuery && (_jsxs("div", { className: "mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-lg", children: [_jsxs("div", { className: "flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold", children: [selectedEmployee.firstName.charAt(0), selectedEmployee.lastName.charAt(0)] }), _jsxs("div", { className: "flex-1", children: [_jsxs("p", { className: "font-medium text-gray-900", children: [selectedEmployee.firstName, " ", selectedEmployee.lastName] }), _jsx("p", { className: "text-sm text-gray-600", children: selectedEmployee.position || '—' })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => setSelectedEmployeeId(''), children: "Change" })] }))] }) })] }), selectedEmployee && (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(Calendar, { className: "h-5 w-5" }), "Date Range"] }) }), _jsxs(CardContent, { className: "space-y-4", children: [_jsxs("div", { className: "flex gap-2 flex-wrap", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: () => handleQuickDateRange('week'), children: "Last 7 Days" }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => handleQuickDateRange('month'), children: "This Month" }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => handleQuickDateRange('lastMonth'), children: "Last Month" }), defaultPayPeriodConfig && (_jsx(Button, { variant: "outline", size: "sm", onClick: () => handleQuickDateRange('payPeriod'), className: "border-blue-300 text-blue-700 hover:bg-blue-50", children: "Current Pay Period" }))] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Start Date" }), _jsx(Input, { type: "date", value: startDate, onChange: (e) => setStartDate(e.target.value) })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "End Date" }), _jsx(Input, { type: "date", value: endDate, onChange: (e) => setEndDate(e.target.value) })] })] })] })] })), selectedEmployee && dailyTotals.length > 0 && (_jsxs("div", { className: "grid gap-4 md:grid-cols-3", children: [_jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Total Hours" }) }), _jsxs(CardContent, { children: [_jsx("div", { className: "text-3xl font-bold text-blue-600", children: formatHours(summaryStats.totalHours) }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "Net work time" })] })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Days Worked" }) }), _jsxs(CardContent, { children: [_jsx("div", { className: "text-3xl font-bold text-green-600", children: summaryStats.daysWorked }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "In selected period" })] })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Break Time" }) }), _jsxs(CardContent, { children: [_jsx("div", { className: "text-3xl font-bold text-yellow-600", children: formatHours(summaryStats.totalBreakTime) }), _jsx("p", { className: "text-xs text-gray-500 mt-1", children: "Total breaks" })] })] })] })), selectedEmployee && (_jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(Clock, { className: "h-5 w-5" }), "Punch Records"] }), _jsxs(CardDescription, { children: [format(parseISO(startDate), 'MMM d, yyyy'), " - ", format(parseISO(endDate), 'MMM d, yyyy')] })] }), _jsx(CardContent, { children: loadingPunches ? (_jsx("div", { className: "space-y-3", children: [1, 2, 3].map(i => (_jsx(Skeleton, { className: "h-20 w-full" }, i))) })) : punchError ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "Failed to load punch history. Please try again." })] })) : dailyTotals.length === 0 ? (_jsxs("div", { className: "text-center py-12", children: [_jsx(Clock, { className: "mx-auto h-12 w-12 text-gray-300 mb-3" }), _jsx("p", { className: "text-gray-600 font-medium", children: "No punches found" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "No time clock activity in the selected date range" })] })) : (_jsx("div", { className: "space-y-6", children: dailyTotals.map((day) => {
                                const dayPunches = punches.filter(p => {
                                    // Use local timezone date for grouping, not UTC punchDate
                                    const punchDate = p.timestamp ? format(parseISO(p.timestamp), 'yyyy-MM-dd') : null;
                                    return punchDate === day.date && p.status !== 'CORRECTED';
                                });
                                const isExpanded = expandedDays.has(day.date);
                                return (_jsxs("div", { className: "border rounded-lg overflow-hidden", children: [_jsxs("button", { onClick: () => toggleDayExpanded(day.date), className: "w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors", children: [_jsxs("div", { className: "flex items-center gap-3", children: [isExpanded ? (_jsx(ChevronDown, { className: "h-5 w-5 text-gray-400" })) : (_jsx(ChevronRight, { className: "h-5 w-5 text-gray-400" })), _jsxs("div", { className: "text-left", children: [_jsx("h3", { className: "font-semibold text-gray-900", children: format(parseISO(day.date), 'EEEE, MMMM d, yyyy') }), _jsxs("p", { className: "text-sm text-gray-500", children: [day.punchCount, " ", day.punchCount === 1 ? 'punch' : 'punches'] })] })] }), _jsxs("div", { className: "text-right", children: [_jsx("div", { className: "text-2xl font-bold text-blue-600", children: formatHours(day.netHours) }), _jsx("p", { className: "text-xs text-gray-500", children: "Net hours" })] })] }), isExpanded && (_jsxs("div", { className: "px-4 pb-4 border-t", children: [_jsxs("div", { className: "grid grid-cols-3 gap-4 py-3 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Total: " }), _jsx("span", { className: "font-medium", children: formatHours(day.hoursWorked) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Break: " }), _jsx("span", { className: "font-medium", children: formatHours(day.breakTime) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Net: " }), _jsx("span", { className: "font-medium text-blue-600", children: formatHours(day.netHours) })] })] }), _jsx("div", { className: "space-y-2 mt-3", children: dayPunches.map((punch) => (_jsxs("div", { className: "flex items-center justify-between p-3 bg-gray-50 rounded-md", children: [_jsxs("div", { className: "flex items-center gap-3", children: [getActionBadge(punch.action), _jsx("span", { className: "text-sm font-medium text-gray-900", children: format(parseISO(punch.timestamp), 'h:mm:ss a') })] }), punch.notes && (_jsx("span", { className: "text-xs text-gray-500", children: punch.notes }))] }, punch.punchId))) })] }))] }, day.date));
                            }) })) })] })), !selectedEmployee && (_jsx(Card, { children: _jsx(CardContent, { className: "py-12", children: _jsxs("div", { className: "text-center", children: [_jsx(User, { className: "mx-auto h-12 w-12 text-gray-300 mb-3" }), _jsx("p", { className: "text-gray-600 font-medium", children: "No employee selected" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Select an employee above to view their punch history" })] }) }) }))] }));
}
